import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { useProfile } from './useProfile';
import { setLastRedirectReason } from '../lib/redirectDebug';
import { api } from '../services/api';
import { SystemUser, UserProfile, SavedReport } from '../types';
import { runtimeCache } from '../lib/runtimeCache';
import { isUuid } from '../lib/isUuid';
import { loadGuestShortlist } from '../lib/guestShortlist';

export type AuthStatus = 'logged_out' | 'onboarding' | 'logged_in';

// DEV-only redirect logging helper
const logRedirect = (from: string, to: string, reason: string) => {
    if (import.meta.env.DEV) {
        console.log(`[Redirect] ${from} → ${to} (${reason})`);
    }
};

// Single source of truth for route determination
// CRITICAL: ONLY uses profiles table fields (first_name/last_name) for completion check
const getNextRoute = (params: {
    currentPath: string;
    userId: string | null;
    profile: any; // Profile object for completion check
    isOnboardingRoute: boolean;
}): string | null => {
    const { currentPath, userId, profile, isOnboardingRoute } = params;
    
    // Standardized completion check: ONLY uses profiles table fields
    // Do NOT use user_metadata or other sources
    const isProfileComplete = !!(profile?.firstName && profile?.lastName);

    if (import.meta.env.DEV) {
        console.log('[getNextRoute] Route decision:', {
            currentPath,
            userId,
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            first_name: profile?.first_name,
            last_name: profile?.last_name,
            isProfileComplete,
            isOnboardingRoute
        });
    }

    // If no user, allow public routes (dashboard, property details, landing)
    // Only redirect to login for protected routes
    if (!userId) {
        // Public routes that don't require auth
        const publicRoutes = ['/', '/login', '/marketplace', '/property', '/projects'];
        const isPublicRoute = publicRoutes.some(route => 
            currentPath === route || 
            currentPath.startsWith('/auth/callback') ||
            currentPath.startsWith('/property/') ||
            currentPath.startsWith('/projects/')
        );
        
        if (isPublicRoute) {
            return null; // Stay on public route
        }
        
        // Protected routes - redirect to login
        return '/login';
    }

    // If user exists and on login, go to marketplace (onboarding disabled)
    if (userId && currentPath === '/login') {
        return '/marketplace';
    }

    // Otherwise stay on current route
    // NOTE: Onboarding redirect is handled by OnboardingView itself, not here
    return null;
};

export const useAppAuth = () => {
    const { user, session, authReady, userId, signOut } = useAuth();
    
    // Core Data from Supabase/Profiles - only fetch if authReady AND userId
    const location = useLocation();
    const shouldFetchProfile = authReady && userId;
    
    const { profile: userProfile, loading: profileLoading, isProfileComplete, refreshProfile, updateProfile: setUserProfile, timeoutError: profileTimeoutError } = useProfile(shouldFetchProfile ? session : null);

    const navigate = useNavigate();

    const [authStatus, setAuthStatus] = useState<AuthStatus>('logged_out');
    const [currentUser, setCurrentUser] = useState<SystemUser | null>(null);
    const [isCalculated, setIsCalculated] = useState(false);
    
    const [shortlist, setShortlist] = useState<(string | number)[]>([]);
    const [savedReports, setSavedReports] = useState<SavedReport[]>([]);

    const redirectDoneRef = useRef(false);
    const lastRedirectRef = useRef<string | null>(null);
    const renderCountRef = useRef(0);

    // Function to load auxiliary data (Shortlist/Reports) - DO NOT call during onboarding
    const loadAuxData = async (userIdParam: string) => {
        // Return immediately if !authReady or !userId
        if (!authReady || !userId) {
            if (import.meta.env.DEV) console.log('[useAppAuth] [GUARD] Skipping loadAuxData: authReady or userId missing');
            return;
        }

        // Onboarding is disabled - always load auxiliary data if user exists

        // Use runtime cache to check if we should fetch shortlists
        // CRITICAL: Also check if shortlist state is actually populated
        // If cache says loaded but state is empty, we need to fetch again
        const cacheSaysLoaded = runtimeCache.isLoaded('shortlists', userIdParam);
        const stateIsEmpty = shortlist.length === 0;
        
        // Reset cache if it says loaded but state is empty (inconsistent state)
        if (cacheSaysLoaded && stateIsEmpty && !runtimeCache.getState().inFlight.shortlists) {
            if (import.meta.env.DEV) {
                console.log('[useAppAuth] [FIX] Cache says loaded but state is empty - resetting cache');
            }
            runtimeCache.resetResource('shortlists', userIdParam);
        }
        
        // GUARD: Only fetch from Supabase if userId is a valid UUID
        // For guests/e2e users, load from localStorage instead
        const isGuestMode = !userIdParam || !isUuid(userIdParam);
        
        if (isGuestMode) {
            // Guest mode: load from localStorage (guestShortlist)
            const guestList = loadGuestShortlist();
            const stringIds = guestList.map(id => String(id));
            setShortlist(stringIds);
            if (import.meta.env.DEV) {
                console.log('[useAppAuth] [GUEST] Loaded shortlist from localStorage:', stringIds.length, 'items');
            }
        } else {
            // Logged-in user: fetch from Supabase
            const shouldFetchShortlist = runtimeCache.shouldFetch('shortlists', userIdParam);
            
            if (!shouldFetchShortlist) {
                if (import.meta.env.DEV) {
                    const state = runtimeCache.getState();
                    console.log('[useAppAuth] [GUARD] Skipping shortlists via cache:', {
                        loaded: state.loaded.shortlists,
                        inFlight: state.inFlight.shortlists,
                        currentShortlistLength: shortlist.length
                    });
                }
            } else {
                try {
                    runtimeCache.markInFlight('shortlists', userIdParam);
                    if (import.meta.env.DEV) console.log('[useAppAuth] [FETCH START] Shortlists for user', userIdParam);
                    
                    const list = await api.user.getShortlist(userIdParam).catch((e) => {
                        if (import.meta.env.DEV) {
                            console.error('[useAppAuth] Shortlist fetch error:', e);
                            const isAuthError = e?.code === '42501' || e?.code === 'PGRST301' ||
                                               (e?.message && (e.message.includes('JWT') || e.message.includes('permission') || e.message.includes('unauthorized')));
                            if (isAuthError) {
                                console.log('[useAppAuth] Shortlist unauthorized, skipping retry');
                            }
                        }
                        return [];
                    });

                    // CRITICAL: Mark cache as loaded BEFORE setState to prevent re-triggers
                    // Only mark as loaded if we actually got data (even if empty array is valid)
                    runtimeCache.markLoaded('shortlists', userIdParam);
                    const stringIds = list.map(id => String(id));
                    setShortlist(stringIds);
                    if (import.meta.env.DEV) {
                      console.log('[useAppAuth] [FETCH END] Shortlists loaded successfully:', stringIds.length, 'items');
                    }
                } catch (e) {
                    console.error("[useAppAuth] Failed to load shortlists", e);
                    runtimeCache.markFailed('shortlists', userIdParam);
                }
            }
        }

        // Use runtime cache to check if we should fetch saved reports
        if (!runtimeCache.shouldFetch('savedReports', userIdParam)) {
            if (import.meta.env.DEV) {
                const state = runtimeCache.getState();
                console.log('[useAppAuth] [GUARD] Skipping savedReports via cache:', {
                    loaded: state.loaded.savedReports,
                    inFlight: state.inFlight.savedReports
                });
            }
        } else {
            try {
                runtimeCache.markInFlight('savedReports', userIdParam);
                if (import.meta.env.DEV) console.log('[useAppAuth] [FETCH START] Saved reports for user', userIdParam);
                
                const reports = await api.user.getReports(userIdParam).catch((e) => {
                    if (import.meta.env.DEV) {
                        console.error('[useAppAuth] Reports fetch error:', e);
                        const isAuthError = e?.code === '42501' || e?.code === 'PGRST301' ||
                                           (e?.message && (e.message.includes('JWT') || e.message.includes('permission') || e.message.includes('unauthorized')));
                        if (isAuthError) {
                            console.log('[useAppAuth] Reports unauthorized, skipping retry');
                        }
                    }
                    return [];
                });

                // CRITICAL: Mark cache as loaded BEFORE setState to prevent re-triggers
                runtimeCache.markLoaded('savedReports', userIdParam);
                setSavedReports(reports);
                if (import.meta.env.DEV) console.log('[useAppAuth] [FETCH END] Saved reports loaded successfully');
            } catch (e) {
                console.error("[useAppAuth] Failed to load saved reports", e);
                runtimeCache.markFailed('savedReports', userIdParam);
            }
        }
    };

    useEffect(() => {
        // Increment render count for debug
        runtimeCache.incrementRender();
        renderCountRef.current++;

        // Reset cache when userId changes
        runtimeCache.reset(userId);

        // Debug logging in DEV
        if (import.meta.env.DEV) {
            const cacheState = runtimeCache.getState();
            const isGuestValue = authStatus === 'logged_out';
            console.log('[useAppAuth] Diagnostic', {
                route: location.pathname,
                isGuest: isGuestValue,
                userId,
                isUuid: userId ? isUuid(userId) : false,
                shortlistLength: shortlist.length,
                profileLoaded: cacheState.loaded.profile,
                shortlistsLoaded: cacheState.loaded.shortlists,
                savedReportsLoaded: cacheState.loaded.savedReports,
                renders: renderCountRef.current
            });
        }

        // Don't do anything until auth is ready
        if (!authReady) {
            if (import.meta.env.DEV) console.log('[useAppAuth] Waiting for authReady');
            return;
        }

        // Load shortlist from localStorage if user is not logged in
        if (!userId && authReady) {
            try {
                const localShortlist = JSON.parse(localStorage.getItem('shortlist') || '[]');
                if (localShortlist.length > 0) {
                    setShortlist(localShortlist.map((id: any) => String(id)));
                    if (import.meta.env.DEV) {
                        console.log('[useAppAuth] Loaded shortlist from localStorage:', localShortlist.length, 'items');
                    }
                }
            } catch (e) {
                if (import.meta.env.DEV) {
                    console.warn('[useAppAuth] Failed to load shortlist from localStorage', e);
                }
            }
        }

        const e2eBypassGuest = import.meta.env.VITE_E2E_BYPASS_GUEST === 'true'
            || (typeof window !== 'undefined' && localStorage.getItem('e2eLoggedIn') === 'true');

        if (e2eBypassGuest) {
            // E2E Test Mode - PRIORITY OVER EVERYTHING
            const fsEmail = (typeof window !== 'undefined' && localStorage.getItem('e2eUserEmail')) || 'e2e@test.local';
            setAuthStatus('logged_in');
            setCurrentUser({
                id: 'e2e-user' as any,
                name: 'E2E Test User',
                email: fsEmail,
                role: 'User',
                status: 'Active',
                lastActive: 'Now',
                avatar: '',
                joinedDate: new Date().toISOString()
            });
            setShortlist([]);
            setSavedReports([]);
            // Auto-redirect if on login page
            if (location.pathname === '/login' && !redirectDoneRef.current && lastRedirectRef.current !== '/marketplace') {
                redirectDoneRef.current = true;
                lastRedirectRef.current = '/marketplace';
                logRedirect('/login', '/marketplace', 'e2e bypass');
                navigate('/marketplace', { replace: true });
            }
            setIsCalculated(true);
            return;
        }

        // Wait for profile to finish loading before making decisions
        if (profileLoading) {
            if (import.meta.env.DEV) console.log('[useAppAuth] Waiting for profile to load');
            return;
        }

        if (user && userId) {
            // 1. Construct SystemUser shim
            const shimUser: SystemUser = {
                id: user.id as any,
                name: userProfile?.name || user.user_metadata.full_name || 'User',
                email: user.email || '',
                role: 'User',
                status: 'Active',
                lastActive: 'Now',
                avatar: userProfile?.avatar || '',
                joinedDate: user.created_at || new Date().toISOString()
            };
            setCurrentUser(shimUser);

            // 2. Always set status to logged_in if user exists (NO onboarding requirement)
            // Users go straight to dashboard after signup/login
            setAuthStatus('logged_in');
            
            if (import.meta.env.DEV) {
                console.log('[useAppAuth] User authenticated - routing to dashboard (onboarding disabled)');
            }

            // 3. Use single source of truth for redirects
            const nextRoute = getNextRoute({
                currentPath: location.pathname,
                userId,
                profile: userProfile,
                isOnboardingRoute: false // Onboarding disabled: false // Onboarding disabled
            });

            if (import.meta.env.DEV && nextRoute) {
                console.log('[useAppAuth] Chosen target route:', nextRoute);
            }

            if (nextRoute && nextRoute !== location.pathname && !redirectDoneRef.current && lastRedirectRef.current !== nextRoute) {
                redirectDoneRef.current = true;
                lastRedirectRef.current = nextRoute;
                const reason = 'user authenticated';
                logRedirect(location.pathname, nextRoute, reason);
                setLastRedirectReason(`useAppAuth -> ${nextRoute} because ${reason}`);
                navigate(nextRoute, { replace: true });
                return; // Exit early after redirect
            }

            // 4. Load auxiliary data (onboarding is disabled, so always load)
            // CRITICAL: Always attempt to load shortlist if state is empty
            // This ensures shortlist appears even if cache state is inconsistent
            if (shortlist.length === 0) {
                // Reset cache to force fetch if state is empty
                runtimeCache.resetResource('shortlists', userId);
            }
            loadAuxData(userId);

        } else {
            // Logged Out - reset all state
            setAuthStatus('logged_out');
            setCurrentUser(null);
            setShortlist([]);
            setSavedReports([]);
            runtimeCache.reset(null);
            redirectDoneRef.current = false;
            lastRedirectRef.current = null;
        } 
        setIsCalculated(true);
    }, [authReady, userId, userProfile, profileLoading, location.pathname, navigate]);

    const handleLogout = async () => {
        await signOut();
        setAuthStatus('logged_out');
        setCurrentUser(null);
        setShortlist([]);
        setSavedReports([]);
        runtimeCache.reset(null);
        redirectDoneRef.current = false;
        lastRedirectRef.current = null;
        if (typeof window !== 'undefined') {
            localStorage.removeItem('e2eLoggedIn');
            localStorage.removeItem('e2eUserEmail');
        }
        navigate('/login', { replace: true });
    };

    return {
        user: currentUser,
        isGuest: authStatus === 'logged_out',
        authLoading: !authReady || profileLoading || !isCalculated,
        authStatus,
        shortlist,
        setShortlist,
        savedReports,
        setSavedReports,
        handleLogout,
        userProfile,
        setUserProfile,
        profileTimeoutError
    };
};
